import { useEffect, useRef } from 'react';

export interface HotkeyDef {
  /** Lowercase key name, e.g. "t", "k", "f", "w" */
  key: string;
  /** Requires Cmd (macOS) or Ctrl (Win/Linux) */
  mod?: boolean;
  /** Requires Shift */
  shift?: boolean;
  /** Allow triggering even when focus is inside an input / textarea */
  allowInInput?: boolean;
  /**
   * Return `false` when the shortcut had nothing to act on: the key's default action is then
   * left alone and other listeners (e.g. a panel's own Escape-to-close) still see the event
   * as unhandled.
   */
  handler: (e: KeyboardEvent) => unknown;
}

/**
 * Registers global keyboard shortcuts.
 * Uses a ref so the listener is only added once and handlers are always fresh.
 */
export function useHotkeys(hotkeys: HotkeyDef[]) {
  const ref = useRef<HotkeyDef[]>(hotkeys);
  ref.current = hotkeys;

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      const target = e.target as HTMLElement | null;
      const inInput =
        !!target &&
        (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);

      // Primary modifier: Cmd on macOS, Ctrl elsewhere
      const primaryMod = e.metaKey || e.ctrlKey;

      for (const hk of ref.current) {
        if (e.key.toLowerCase() !== hk.key.toLowerCase()) continue;
        if (hk.mod && !primaryMod) continue;
        if (!hk.mod && primaryMod) continue;
        if (hk.shift !== undefined) {
          if (hk.shift && !e.shiftKey) continue;
          if (!hk.shift && e.shiftKey) continue;
        }
        if (inInput && !hk.allowInInput) continue;

        if (hk.handler(e) !== false) e.preventDefault();
        break;
      }
    }

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, []);
}
